import {
  authenticatedApi,
  ApiError,
  API_BASE_URL
} from './apiUtils';

export const meetingsApi = {
  // ============================================
  // Lookup Tables (Read-Only)
  // ============================================

  getMeetingTypes: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/meetings/types`);
    } catch (error) {
      throw new ApiError('Failed to fetch meeting types', error.status || 500);
    }
  },

  getAttendeeTypes: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/meetings/attendee-types`);
    } catch (error) {
      throw new ApiError('Failed to fetch attendee types', error.status || 500);
    }
  },

  getMeetingStatuses: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/meetings/statuses`);
    } catch (error) {
      throw new ApiError('Failed to fetch meeting statuses', error.status || 500);
    }
  },

  getMeetingRelationTypes: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/meetings/relation-types`);
    } catch (error) {
      throw new ApiError('Failed to fetch meeting relation types', error.status || 500);
    }
  },

  getSystemTalkingPoints: async (meetingTypeIds = null) => {
    try {
      let url = `${API_BASE_URL}/meetings/talking-points`;
      if (meetingTypeIds && meetingTypeIds.length > 0) {
        url += `?meetingTypeIds=${meetingTypeIds.join(',')}`;
      }
      return await authenticatedApi.get(url);
    } catch (error) {
      throw new ApiError('Failed to fetch system talking points', error.status || 500);
    }
  },

  getAllSystemTalkingPoints: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/meetings/talking-points/all`);
    } catch (error) {
      throw new ApiError('Failed to fetch all system talking points', error.status || 500);
    }
  },

  // ============================================
  // Meeting CRUD
  // ============================================

  getAllMeetings: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.cursor) queryParams.append('cursor', params.cursor);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.orderBy) queryParams.append('orderBy', params.orderBy);
      if (params.orderDirection) queryParams.append('orderDirection', params.orderDirection);
      if (params.search) queryParams.append('search', params.search);
      if (params.statusFilter) queryParams.append('statusFilter', params.statusFilter);
      if (params.participantIds) queryParams.append('participantIds', params.participantIds);
      if (params.datePreset) queryParams.append('datePreset', params.datePreset);
      if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
      if (params.dateTo) queryParams.append('dateTo', params.dateTo);
      if (params.typeIds) queryParams.append('typeIds', params.typeIds);
      if (params.actionItemsFilter) queryParams.append('actionItemsFilter', params.actionItemsFilter);
      if (params.talkingPointsFilter) queryParams.append('talkingPointsFilter', params.talkingPointsFilter);

      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/meetings${queryString ? `?${queryString}` : ''}`;
      return await authenticatedApi.get(url);
    } catch (error) {
      throw new ApiError('Failed to fetch meetings', error.status || 500);
    }
  },

  getMyMeetings: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.cursor) queryParams.append('cursor', params.cursor);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.orderBy) queryParams.append('orderBy', params.orderBy);
      if (params.orderDirection) queryParams.append('orderDirection', params.orderDirection);
      if (params.search) queryParams.append('search', params.search);
      if (params.statusFilter) queryParams.append('statusFilter', params.statusFilter);
      if (params.participantIds) queryParams.append('participantIds', params.participantIds);
      if (params.datePreset) queryParams.append('datePreset', params.datePreset);
      if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
      if (params.dateTo) queryParams.append('dateTo', params.dateTo);
      if (params.typeIds) queryParams.append('typeIds', params.typeIds);
      if (params.actionItemsFilter) queryParams.append('actionItemsFilter', params.actionItemsFilter);
      if (params.talkingPointsFilter) queryParams.append('talkingPointsFilter', params.talkingPointsFilter);

      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/meetings/me${queryString ? `?${queryString}` : ''}`;
      return await authenticatedApi.get(url);
    } catch (error) {
      throw new ApiError('Failed to fetch my meetings', error.status || 500);
    }
  },

  getMeetingById: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/meetings/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Meeting not found', 404);
      }
      throw new ApiError('Failed to fetch meeting', error.status || 500);
    }
  },

  createMeeting: async (meetingData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/meetings`, meetingData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid meeting data', 400);
      }
      throw new ApiError('Failed to create meeting', error.status || 500);
    }
  },

  updateMeeting: async (id, meetingData) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/meetings/${id}`, meetingData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid meeting data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Meeting not found', 404);
      }
      if (error.status === 403) {
        throw new ApiError('You are not authorized to update this meeting', 403);
      }
      throw new ApiError('Failed to update meeting', error.status || 500);
    }
  },

  deleteMeeting: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/meetings/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Meeting not found', 404);
      }
      if (error.status === 403) {
        throw new ApiError('You are not authorized to delete this meeting', 403);
      }
      throw new ApiError(error.data?.error || 'Failed to delete meeting', error.status || 500);
    }
  },

  updateMeetingStatus: async (id, statusId) => {
    try {
      return await authenticatedApi.patch(`${API_BASE_URL}/meetings/${id}/status`, { statusId });
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Meeting not found', 404);
      }
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid status', 400);
      }
      throw new ApiError('Failed to update meeting status', error.status || 500);
    }
  },

  getMeetingStatusHistory: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/meetings/${id}/status-history`);
    } catch (error) {
      throw new ApiError('Failed to fetch meeting status history', error.status || 500);
    }
  },

  getPreviousMeetings: async (meetingId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.cursor) queryParams.append('cursor', params.cursor);

      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/meetings/${meetingId}/previous${queryString ? `?${queryString}` : ''}`;
      return await authenticatedApi.get(url);
    } catch (error) {
      throw new ApiError('Failed to fetch previous meetings', error.status || 500);
    }
  },

  // ============================================
  // Meeting Attendees
  // ============================================

  getAttendees: async (meetingId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/meetings/${meetingId}/attendees`);
    } catch (error) {
      throw new ApiError('Failed to fetch attendees', error.status || 500);
    }
  },

  addAttendee: async (meetingId, attendeeData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/meetings/${meetingId}/attendees`, attendeeData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid attendee data', 400);
      }
      if (error.status === 403) {
        throw new ApiError('You are not authorized to add attendees', 403);
      }
      throw new ApiError('Failed to add attendee', error.status || 500);
    }
  },

  updateAttendee: async (meetingId, employeeId, attendeeData) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/meetings/${meetingId}/attendees/${employeeId}`, attendeeData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid attendee data', 400);
      }
      if (error.status === 403) {
        throw new ApiError('You are not authorized to update attendees', 403);
      }
      throw new ApiError('Failed to update attendee', error.status || 500);
    }
  },

  removeAttendee: async (meetingId, employeeId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/meetings/${meetingId}/attendees/${employeeId}`);
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError('You are not authorized to remove attendees', 403);
      }
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Cannot remove attendee', 400);
      }
      throw new ApiError('Failed to remove attendee', error.status || 500);
    }
  },

  // ============================================
  // Meeting Talking Points
  // ============================================

  getMeetingTalkingPoints: async (meetingId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/meetings/${meetingId}/talking-points`);
    } catch (error) {
      throw new ApiError('Failed to fetch talking points', error.status || 500);
    }
  },

  addMeetingTalkingPoint: async (meetingId, talkingPointData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/meetings/${meetingId}/talking-points`, talkingPointData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid talking point data', 400);
      }
      throw new ApiError('Failed to add talking point', error.status || 500);
    }
  },

  updateMeetingTalkingPoint: async (meetingId, talkingPointId, talkingPointData) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/meetings/${meetingId}/talking-points/${talkingPointId}`, talkingPointData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid talking point data', 400);
      }
      throw new ApiError('Failed to update talking point', error.status || 500);
    }
  },

  removeMeetingTalkingPoint: async (meetingId, talkingPointId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/meetings/${meetingId}/talking-points/${talkingPointId}`);
    } catch (error) {
      throw new ApiError('Failed to remove talking point', error.status || 500);
    }
  },

  // ============================================
  // Meeting Notes
  // ============================================

  getMeetingNotes: async (meetingId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.type) queryParams.append('type', params.type);
      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/meetings/${meetingId}/notes${queryString ? `?${queryString}` : ''}`;
      return await authenticatedApi.get(url);
    } catch (error) {
      throw new ApiError('Failed to fetch notes', error.status || 500);
    }
  },

  addMeetingNote: async (meetingId, noteData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/meetings/${meetingId}/notes`, noteData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid note data', 400);
      }
      throw new ApiError('Failed to add note', error.status || 500);
    }
  },

  updateMeetingNote: async (meetingId, noteId, noteData) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/meetings/${meetingId}/notes/${noteId}`, noteData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid note data', 400);
      }
      if (error.status === 403) {
        throw new ApiError('You are not authorized to update this note', 403);
      }
      throw new ApiError('Failed to update note', error.status || 500);
    }
  },

  removeMeetingNote: async (meetingId, noteId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/meetings/${meetingId}/notes/${noteId}`);
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError('You are not authorized to delete this note', 403);
      }
      throw new ApiError('Failed to remove note', error.status || 500);
    }
  },

  // ============================================
  // Action Items
  // ============================================

  getActionItems: async (meetingId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/meetings/${meetingId}/action-items`);
    } catch (error) {
      throw new ApiError('Failed to fetch action items', error.status || 500);
    }
  },

  addActionItem: async (meetingId, actionItemData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/meetings/${meetingId}/action-items`, actionItemData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid action item data', 400);
      }
      throw new ApiError('Failed to add action item', error.status || 500);
    }
  },

  updateActionItem: async (meetingId, actionItemId, actionItemData) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/meetings/${meetingId}/action-items/${actionItemId}`, actionItemData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid action item data', 400);
      }
      if (error.status === 403) {
        throw new ApiError('You are not authorized to update this action item', 403);
      }
      throw new ApiError('Failed to update action item', error.status || 500);
    }
  },

  removeActionItem: async (meetingId, actionItemId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/meetings/${meetingId}/action-items/${actionItemId}`);
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError('You are not authorized to delete this action item', 403);
      }
      throw new ApiError('Failed to remove action item', error.status || 500);
    }
  },

  // ============================================
  // Meeting References (Linked Items)
  // ============================================

  getMeetingReferences: async (meetingId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/meetings/${meetingId}/references`);
    } catch (error) {
      throw new ApiError('Failed to fetch references', error.status || 500);
    }
  },

  addMeetingReference: async (meetingId, referenceData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/meetings/${meetingId}/references`, referenceData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid reference data', 400);
      }
      throw new ApiError('Failed to add reference', error.status || 500);
    }
  },

  removeMeetingReference: async (meetingId, referenceId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/meetings/${meetingId}/references/${referenceId}`);
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError('You are not authorized to remove this reference', 403);
      }
      throw new ApiError('Failed to remove reference', error.status || 500);
    }
  },

  // ============================================
  // Talking Point References (Linked Items)
  // ============================================

  getTalkingPointReferences: async (meetingId, talkingPointId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/meetings/${meetingId}/talking-points/${talkingPointId}/references`);
    } catch (error) {
      throw new ApiError('Failed to fetch talking point references', error.status || 500);
    }
  },

  addTalkingPointReference: async (meetingId, talkingPointId, referenceData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/meetings/${meetingId}/talking-points/${talkingPointId}/references`, referenceData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid reference data', 400);
      }
      throw new ApiError('Failed to add talking point reference', error.status || 500);
    }
  },

  removeTalkingPointReference: async (meetingId, talkingPointId, referenceId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/meetings/${meetingId}/talking-points/${talkingPointId}/references/${referenceId}`);
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError('You are not authorized to remove this reference', 403);
      }
      throw new ApiError('Failed to remove talking point reference', error.status || 500);
    }
  },
};
